import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faSortAmountDown, faSortAmountUp } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import DataTable from './shared/DataTable';
import OrganizationSidebar from './OrganizationSidebar';

interface Organization {
  id: number;
  name: string;
  duns: string;
  data_scraped_at?: string;
  amount?: number;
  created_at: string;
  uei?: string;
  recipient_level?: string;
  is_favorited?: boolean;
  metadata?: {
    location?: {
      address_line1?: string;
      city_name?: string;
      state_code?: string;
      zip?: string;
    };
    business_types?: string[];
    alternate_names?: string[];
  };
}

interface PaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Filters {
  status: string;
  type: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  showFavorites: boolean;
  states: string[];
  businessTypes: string[];
}

interface Column {
  key: string;
  header: string;
  render?: (value: any, row?: any) => React.ReactNode;
}

const Organizations: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [meta, setMeta] = useState<PaginationMeta>({ current_page: 1, total_pages: 1, total_count: 0 });
  const [filters, setFilters] = useState<Filters>({
    status: '',
    type: '',
    sortBy: 'name',
    sortOrder: 'asc',
    showFavorites: false,
    states: [],
    businessTypes: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  const [facets, setFacets] = useState<{ states: string[]; businessTypes: string[] }>({
    states: [],
    businessTypes: [],
  });

  const fetchOrganizations = async (page: number, search?: string) => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const filterParams = new URLSearchParams({
        status: filters.status,
        type: filters.type,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder,
        states: filters.states.join(','),
        business_types: filters.businessTypes.join(','),
        is_favorited: filters.showFavorites ? 'true' : 'false',
      }).toString();

      const response = await fetch(`/api/usa_spending_recipients?page=${page}${searchParam}&${filterParams}`);
      const data = await response.json();
      let filteredRecipients = data.recipients;

      // Update facets
      const uniqueStates = new Set<string>();
      const uniqueBusinessTypes = new Set<string>();

      data.recipients.forEach((org: Organization) => {
        if (org.metadata?.location?.state_code) {
          uniqueStates.add(org.metadata.location.state_code);
        }
        if (org.metadata?.business_types) {
          org.metadata.business_types.forEach((type) => uniqueBusinessTypes.add(type));
        }
      });

      setFacets({
        states: Array.from(uniqueStates).sort(),
        businessTypes: Array.from(uniqueBusinessTypes).sort(),
      });

      // Apply favorites filter client-side since it's based on the user's state
      if (filters.showFavorites) {
        filteredRecipients = filteredRecipients.filter((r: Organization) => r?.is_favorited === true);
      }

      setOrganizations(filteredRecipients);
      setMeta({
        ...data.meta,
        total_count: filteredRecipients.length,
        total_pages: Math.ceil(filteredRecipients.length / 100),
      });
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchOrganizations(1, searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filters]);

  const handlePageChange = (page: number) => {
    fetchOrganizations(page, searchTerm);
  };

  const handleToggleFavorite = async (organization: Organization, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const method = organization.is_favorited ? 'DELETE' : 'POST';
      const response = await fetch(`/api/usa_spending_recipients/${organization.id}/favorite`, {
        method,
      });
      const data = await response.json();
      setOrganizations((orgs) =>
        orgs.map((org) => (org.id === organization.id ? { ...org, is_favorited: data.is_favorited } : org)),
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const columns: Column[] = [
    {
      key: 'favorite',
      header: '',
      render: (value: any, row: Organization) => (
        <button
          onClick={(e) => handleToggleFavorite(row, e)}
          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <i className={`fa-${row?.is_favorited ? 'solid' : 'regular'} fa-star text-yellow-500`} />
        </button>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (value: string, row: Organization) => (
        <div className="flex items-center gap-2 max-w-lg truncate">
          <div className={`font-medium ${row?.is_favorited ? 'text-yellow-600 dark:text-yellow-400' : ''}`}>
            {value ? value : 'N/A'}
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Total Amount',
      render: (value: number) => {
        return value
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              notation: 'standard',
            }).format(value)
          : 'N/A';
      },
    },
    {
      key: 'location',
      header: 'Location',
      render: (value: any, row: Organization) => {
        if (!row?.metadata?.location) return 'N/A';
        const { city_name, state_code } = row.metadata.location;
        return [city_name, state_code].filter(Boolean).join(', ') || 'N/A';
      },
    },
    {
      key: 'types',
      header: 'Business Types',
      render: (value: any, row: Organization) => {
        if (!row?.metadata?.business_types?.length) return 'N/A';
        return (
          <div className="flex flex-wrap gap-1">
            {row.metadata.business_types.map((type, index) => (
              <span key={index} className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700">
                {type}
              </span>
            ))}
          </div>
        );
      },
    },
  ];

  const handleRowClick = (row: Organization) => {
    navigate(`/organizations/${row.id}`);
  };

  const toggleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleFilter = (type: 'states' | 'businessTypes', value: string) => {
    setFilters((prev) => {
      const currentValues = prev[type];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return { ...prev, [type]: newValues };
    });
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent form submission
    fetchOrganizations(1, searchTerm);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full dark:text-gray-200">
        Loading organizations...
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900">
      {/* <OrganizationSidebar facets={facets} filters={filters} onFilterToggle={toggleFilter} /> */}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header Section */}
        <div className="p-4 bg-white dark:bg-gray-800 shadow">
          <div className="mx-auto">
            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-grow flex gap-4">
                <form onSubmit={handleSearch} className="flex-grow relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faSearch} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search organizations..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </form>
                <button
                  onClick={() => setFilters((prev) => ({ ...prev, showFavorites: !prev.showFavorites }))}
                  className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg ${
                    filters.showFavorites
                      ? 'bg-yellow-50 border-yellow-300 text-yellow-700 dark:bg-yellow-900 dark:border-yellow-600 dark:text-yellow-200'
                      : 'bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200'
                  } hover:bg-gray-50 dark:hover:bg-gray-600`}
                >
                  <i
                    className={`fa-${filters.showFavorites ? 'solid' : 'regular'} fa-star ${
                      filters.showFavorites ? 'text-yellow-500' : ''
                    }`}
                  />
                  {filters.showFavorites ? 'Showing Favorites' : 'Show Favorites'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="flex-grow overflow-hidden">
          <div className="h-full bg-white dark:bg-gray-800 shadow-sm">
            {organizations.length > 0 ? (
              <div className="h-full overflow-auto">
                <DataTable data={organizations} columns={columns} onRowClick={handleRowClick} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                No organizations found
              </div>
            )}
          </div>
        </div>

        {/* Pagination Section */}
        {meta.total_pages > 1 && (
          <div className="bg-white dark:bg-gray-800 border-t dark:border-gray-700 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(meta.current_page - 1)}
                  disabled={meta.current_page === 1}
                  className="px-3 py-1 rounded transition-colors duration-150 disabled:opacity-50 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {[...Array(meta.total_pages)].map((_, index) => {
                    const page = index + 1;
                    if (
                      page === 1 ||
                      page === meta.total_pages ||
                      (page >= meta.current_page - 2 && page <= meta.current_page + 2)
                    ) {
                      return (
                        <button
                          key={index}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 rounded transition-colors duration-150 ${
                            meta.current_page === page
                              ? 'bg-blue-600 text-white dark:bg-blue-500'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }
                    return null;
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(meta.current_page + 1)}
                  disabled={meta.current_page === meta.total_pages}
                  className="px-3 py-1 rounded transition-colors duration-150 disabled:opacity-50 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Next
                </button>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {meta.current_page} of {meta.total_pages}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Organizations;
